/**
 * 21. General Ledger — posted ledger lines by account and date, newest
 * first, each opening its journal entry.
 *
 * The ledger stores base amounts, in the legal entity's functional currency;
 * `currency_code` on a row is the entry's transaction currency. Amounts are
 * labelled with `functionalCurrencyCode` (CUR-1, CUR-2).
 *
 * REP-FIELDS (D666, RT-D6): the screen reads exactly what GET
 * /v1/general-ledger sends: `entryDay` (the calendar day, never an instant),
 * `entryNumber`, `accountCode`, `accountName`, `debitAmount`,
 * `creditAmount`. The account, from and to filters are the route's own
 * parameters (they used to be inputs with no handler), kept in the address
 * so a trial balance row opens its account here. The route sends no running
 * balance and no opening row yet, so there is no such column, and the page
 * says why instead of showing a blank one. Pages follow the API's cursor;
 * the page's row count is never shown as the size of the ledger. Each legal
 * entity has its own chart, so with more than one entity the account choices
 * are grouped under the entity; a chart longer than the 200 accounts listed
 * says so.
 *
 * Arabic and English (C7): every word from the catalogs; a row's source is
 * named as the kind of record it is, never its stored type.
 */
import { Link, useSearchParams } from "react-router-dom";
import {
  Card,
  PageHeader,
  Skeleton,
  DataTable,
  Toolbar,
  Field,
  Input,
  Select,
} from "@/components/ui";
import { CursorPager } from "@/components/CursorPager";
import { usePendingReason } from "@/components/PendingAction";
import { QueryError } from "@/components/QueryError";
import { useApiList } from "@/lib/api/hooks";
import { useCursorList, withQuery } from "@/lib/api/use-cursor-list";
import { useAuthStore } from "@/lib/auth/store";
import { isZeroDecimal } from "@/lib/decimal-text";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { recordTypeLabel } from "@/lib/i18n/records";
import { formatMoney, formatDate } from "@/lib/money";

/** One row of GET /v1/general-ledger, as the route sends it (camelCased). */
interface LedgerRow {
  id: string;
  journalEntryId: string;
  /** The entry date as the calendar day it is, "2026-10-05". */
  entryDay: string;
  entryNumber: string | null;
  accountCode: string;
  accountName: string;
  /** Base amounts, in `functionalCurrencyCode`. */
  debitAmount: string;
  creditAmount: string;
  sourceDocumentType: string | null;
  /** The books' currency: what the ledger's (base) amounts are in. */
  functionalCurrencyCode: string;
}

/** An account to filter by (GET /v1/accounts). */
interface AccountOption {
  id: string;
  code: string;
  name: string;
  /** Codes repeat per legal entity: each entity has its own chart. */
  legalEntityId: string;
}

/** A legal entity, to name the group its accounts are listed under. */
interface EntityOption {
  id: string;
  name: string;
}

/** The account choices, grouped by legal entity in the order the route lists them. */
function byEntity(
  accounts: AccountOption[],
): Array<{ entityId: string; accounts: AccountOption[] }> {
  const groups = new Map<string, AccountOption[]>();
  for (const a of accounts) {
    const group = groups.get(a.legalEntityId);
    if (group) group.push(a);
    else groups.set(a.legalEntityId, [a]);
  }
  return [...groups.entries()].map(([entityId, list]) => ({
    entityId,
    accounts: list,
  }));
}

/** The slice that brings the running balance and the opening row. */
const RUNNING_BALANCE_SLICE = "AE-ST1";

function amount(value: string, currency: string): string {
  return isZeroDecimal(value) ? "—" : formatMoney(value, currency);
}

export function GeneralLedgerPage() {
  const { t } = useTranslation();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const runningWhy = usePendingReason("statementEngine", RUNNING_BALANCE_SLICE);
  const [params, setParams] = useSearchParams();
  const accountId = params.get("accountId") ?? "";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const setFilter = (name: string, value: string) => {
    const nextParams = new URLSearchParams(params);
    if (value) nextParams.set(name, value);
    else nextParams.delete(name);
    setParams(nextParams, { replace: true });
  };

  const filters = [accountId, from, to].some(Boolean)
    ? ["finance", "general-ledger", accountId, from, to]
    : ["finance", "general-ledger"];
  // The route's own filters, from the address.
  const ledger = useCursorList<LedgerRow>(
    filters,
    withQuery("/finance/general-ledger", {
      accountId,
      entryDateFrom: from,
      entryDateTo: to,
    }),
  );
  const { data, isPending, error, refetch, isFetching } = ledger;

  const canChooseAccount = hasPermission("account.read");
  const accounts = useApiList<AccountOption>(
    ["finance", "accounts", "ledger-filter"],
    "/finance/accounts?limit=200",
    { enabled: canChooseAccount },
  );
  const accountOptions = accounts.data?.items ?? [];
  const chosenIsListed = accountOptions.some((a) => a.id === accountId);
  // One chart per legal entity, so the same code can be listed twice: with
  // more than one entity the choices are grouped under the entity's name
  // (or its place in the list, when the names cannot be read).
  const entityGroups = byEntity(accountOptions);
  const manyEntities = entityGroups.length > 1;
  const entities = useApiList<EntityOption>(
    ["org", "legal-entities"],
    "/legal-entities",
    {
      enabled: manyEntities && hasPermission("organization.legal_entity.read"),
    },
  );
  const entityName = new Map(
    (entities.data?.items ?? []).map((e) => [e.id, e.name]),
  );
  // The route lists up to 200 accounts at once; a longer chart is said so,
  // never shown as if it were the whole chart.
  const accountsCutOff =
    accounts.data !== undefined && accounts.data.total > accountOptions.length;
  const optionOf = (a: AccountOption) => (
    <option key={a.id} value={a.id}>
      {`${a.code} — ${a.name}`}
    </option>
  );
  const canOpenEntry = hasPermission("journal-entry.read");

  return (
    <div>
      <PageHeader
        title={t("finance.ledger.title")}
        subtitle={t("finance.ledger.subtitle")}
      />
      <Toolbar>
        <Field
          label={t("fields.account")}
          error={
            accounts.error ? t("finance.ledger.accountsNotLoaded") : undefined
          }
          hint={
            accountsCutOff
              ? t("finance.ledger.accountsCutOff", {
                  shown: accountOptions.length,
                  total: accounts.data?.total ?? 0,
                })
              : undefined
          }
        >
          <Select
            value={accountId}
            onChange={(e) => setFilter("accountId", e.target.value)}
          >
            <option value="">{t("finance.ledger.allAccounts")}</option>
            {accountId && !chosenIsListed && (
              <option value={accountId}>
                {t("finance.ledger.chosenAccount")}
              </option>
            )}
            {manyEntities
              ? entityGroups.map((group, i) => (
                  <optgroup
                    key={group.entityId}
                    label={
                      entityName.get(group.entityId) ??
                      t("finance.ledger.entityNumber", { number: i + 1 })
                    }
                  >
                    {group.accounts.map(optionOf)}
                  </optgroup>
                ))
              : accountOptions.map(optionOf)}
          </Select>
        </Field>
        <Field label={t("fields.from")}>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFilter("from", e.target.value)}
          />
        </Field>
        <Field label={t("fields.to")}>
          <Input
            type="date"
            value={to}
            onChange={(e) => setFilter("to", e.target.value)}
          />
        </Field>
      </Toolbar>
      {isPending ? (
        <Skeleton rows={12} />
      ) : error || !data ? (
        <QueryError
          error={error}
          retrying={isFetching}
          onRetry={() => void refetch()}
        />
      ) : (
        <Card
          title={
            ledger.page === 1 && !ledger.hasMore
              ? listTitle(t("finance.ledger.list"), data.items.length)
              : t("finance.ledger.list")
          }
        >
          <div role="status" style={{ marginBlockEnd: 12 }}>
            <p style={{ marginBlock: "0 4px" }}>
              {t("finance.ledger.runningBalanceNotShown")}
            </p>
            <p style={{ margin: 0 }}>{runningWhy}</p>
          </div>
          <DataTable<LedgerRow>
            rows={data.items}
            columns={[
              {
                key: "entryDay",
                label: t("fields.date"),
                render: (r) => formatDate(r.entryDay),
              },
              {
                key: "entryNumber",
                label: t("finance.ledger.entry"),
                render: (r) =>
                  !r.entryNumber ? (
                    "—"
                  ) : canOpenEntry ? (
                    <Link
                      className="atlas-mono"
                      to={`/app/finance/journal-entries/${encodeURIComponent(r.journalEntryId)}`}
                    >
                      {r.entryNumber}
                    </Link>
                  ) : (
                    <span className="atlas-mono">{r.entryNumber}</span>
                  ),
              },
              {
                key: "accountCode",
                label: t("fields.account"),
                render: (r) => (
                  <span className="atlas-mono">{r.accountCode}</span>
                ),
              },
              { key: "accountName", label: t("fields.name") },
              {
                key: "debitAmount",
                label: t("fields.debit"),
                numeric: true,
                render: (r) => amount(r.debitAmount, r.functionalCurrencyCode),
              },
              {
                key: "creditAmount",
                label: t("fields.credit"),
                numeric: true,
                render: (r) => amount(r.creditAmount, r.functionalCurrencyCode),
              },
              {
                key: "sourceDocumentType",
                label: t("fields.source"),
                render: (r) =>
                  r.sourceDocumentType
                    ? recordTypeLabel(t, r.sourceDocumentType)
                    : "—",
              },
            ]}
          />
          <CursorPager
            page={ledger.page}
            hasMore={ledger.hasMore}
            next={ledger.next}
            previous={ledger.previous}
            busy={isFetching}
          />
        </Card>
      )}
    </div>
  );
}
